import { useExamCenter } from '@exam-center/exam-center-details';
import {
  useSearchExamCenterInReviewProctorRequestsQuery,
  FilterOperator,
  ExamCenterProctorRequestStep,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import { type GridRowParams, type GridEventListener, NoRowsOverlay } from '@shared/components';

import {
  BaseProctorRequestList,
  inReviewColumns,
  type InReviewProctorRequest,
} from '../../components';
import { ORDER_TYPE, PROCTOR_TYPE_VARIANT_MAP } from '../../constants';
import { useProctorRequestListSearchParams } from '../../hooks';
import { EmptyProctorRequestsOverlay } from '../empty-proctor-requests-overlay';

export default function InReviewProctorRequestList() {
  const { examCenter } = useExamCenter();
  const navigate = useNavigate();

  const [
    { page, pageSize, sortOrder, search, proctorType },
    { changePageNumber, changePageSize, changeSorting },
  ] = useProctorRequestListSearchParams();

  const { data, loading } = useSearchExamCenterInReviewProctorRequestsQuery({
    variables: {
      searchExamCenterProctorRequestsInput: {
        paging: {
          currentPage: page,
          pageSize: pageSize,
        },
        order: {
          field: 'requestedAt',
          type: ORDER_TYPE[sortOrder],
        },
        filter: {
          searchText: {
            fields: ['fullName'],
            value: search,
          },
          conditions: [
            {
              field: 'examCenterCode',
              value: examCenter.code,
              operator: FilterOperator.Equal,
            },
            {
              field: 'step',
              value: [
                ExamCenterProctorRequestStep.InReview_1,
                ExamCenterProctorRequestStep.InReview_2,
              ],
              operator: FilterOperator.In,
            },
            {
              field: 'type',
              value: proctorType.length > 0 ? proctorType : null,
              operator: FilterOperator.In,
            },
          ].filter((condition) => condition.value !== null),
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const examCenterProctorRequests =
    data?.ecSearchExamCenterProctorRequests.examCenterProctorRequests ?? [];

  const examCenterProctorRequestItems = examCenterProctorRequests.map((item) => ({
    id: item.id,
    refCode: item.refCode,
    name: [item.titleTh, item.firstNameTh, item.middleNameTh, item.lastNameTh]
      .filter(Boolean)
      .join(' '),
    type: PROCTOR_TYPE_VARIANT_MAP[item.type],
    status: 'in-review',
    requestedAt: item.requestedAt,
  }));

  const totalCount = data?.ecSearchExamCenterProctorRequests.total ?? 0;

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<InReviewProctorRequest>,
  ) => {
    navigate(`/:examCenterCode/exam-proctors/requests/:requestId`, {
      params: {
        requestId: params.row.id,
        examCenterCode: examCenter.code,
      },
    });
  };

  const isSearchingOrFiltering = search.length > 0 || proctorType.length > 0;

  return (
    <BaseProctorRequestList
      columns={inReviewColumns}
      loading={loading}
      rows={examCenterProctorRequestItems}
      sortingMode='server'
      slots={{
        noRowsOverlay: () => {
          if (isSearchingOrFiltering) {
            return <NoRowsOverlay />;
          }

          return <EmptyProctorRequestsOverlay title='ยังไม่มีคำขอผู้คุมสอบที่อยู่ระหว่างอนุมัติ' />;
        },
      }}
      slotProps={{
        footer: {
          total: totalCount,
          pagination: {
            page: page,
            count: totalCount,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onRowClick={handleRowClick}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];
        if (sortModel?.field && sortModel?.sort) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: sortModel.field,
          });
        }

        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
    />
  );
}
